import { useState } from 'react';
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { faAngleLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  ButtonText,
  FormFeedback,
  Input,
  RadioCard
} from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch
} from 'react-hook-form';
import {
  IGroupedWorkPhases,
  IPermissionLevel,
  IWorkPhase,
  workPhaseMap
} from '@publishing-engine/domain/models';

import { fetchWorkspaceEditorialPhases } from 'src/api/queries';
import { FieldLabel } from 'src/components/Field';
import { Layout } from 'src/components/Layout';
import { Overlay } from 'src/components/Overlay';
import { buildPhasePermissions } from 'src/utils/phasePermission';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import { IRoleFormData, IRolePayload, roleFormSchema } from './roleFormSchema';

type AccessLevel = {
  value: IPermissionLevel;
  title: string;
  description: string;
};

/** Access level offered for every workflow phase, from least to most access. */
const ACCESS_LEVELS: AccessLevel[] = [
  {
    value: 'viewer',
    title: 'Observe',
    description:
      'Ideal for monitoring progress. For team members needing to observe only.'
  },
  {
    value: 'task_completer',
    title: 'Support',
    description:
      'Can complete tasks but not make decisions in this phase. For assistants and support roles.'
  },
  {
    value: 'decision_maker',
    title: 'Decide',
    description:
      'Complete tasks and make decisions in this phase. For editors and managers.'
  }
];

/**
 * What each grouped phase covers. The keys are the editorial phases the backend
 * groups the work phases under, see getGroupedWorkPhases() in the submission
 * service.
 */
const PHASE_DESCRIPTIONS: Partial<Record<IWorkPhase, string>> = {
  assignment: 'Assigning an owner to the submission.',
  initialAssessment: 'A full assessment of this submission must be made.',
  peerReview: 'Inviting reviewers to kick start peer review phase.',
  revisionAssessment:
    "Assess the Author's revised work and make a decision. Be ready to manage Reviewer deadlines and extension requests.",
  preProduction:
    'Ensure the Submission contains all necessary materials to go into Production.',
  rejected: 'After rejecting a submission it will appear in Rejected phase.',
  withdrawn:
    'When a submission is withdrawn by either an author or a staff member.',
  formalAcceptance: 'When a submission formally accepted to be published.'
};

/**
 * What each access level grants, shown once on the access levels label rather
 * than repeated on every phase label. It is what keeps the phase rows down to a
 * single line each.
 */
const AccessLevelsHelp = () => (
  <>
    <Text as="span" display="block" textStyle="headingItem" mb={2}>
      What each access level means?
    </Text>
    {ACCESS_LEVELS.map(({ title, description }) => (
      <Text as="span" display="block" key={title} _notLast={{ mb: 2 }}>
        <Text as="span" display="block" textStyle="headingItem">
          {title}
        </Text>
        <Text as="span" display="block">
          {description}
        </Text>
      </Text>
    ))}
  </>
);

const AccessLevelGroup = ({
  groupName,
  selectedLevel
}: {
  groupName: IWorkPhase;
  selectedLevel?: IPermissionLevel;
}) => {
  const {
    register,
    formState: { errors }
  } = useFormContext<IRoleFormData>();

  return (
    <Flex flexDirection="column" gap={2}>
      <FieldLabel
        label={workPhaseMap[groupName]}
        subText=""
        isRequired={true}
        helpText={PHASE_DESCRIPTIONS[groupName]}
      />
      <Flex gap={4} flexDirection={{ base: 'column', md: 'row' }}>
        {ACCESS_LEVELS.map(({ value, title }) => (
          <Flex key={value} flex="1">
            <RadioCard
              ariaLabel={`${groupName} ${value} option`}
              title={title}
              // The design system requires a body; it stays empty because the
              // level descriptions live in the phase label tooltip.
              body={null}
              value={value}
              register={register(`permissionsGroup.${groupName}`)}
              checked={selectedLevel === value}
              styles={{
                padding: 3,
                ...(selectedLevel === value && {
                  background: 'actionSecondaryFill'
                })
              }}
              width="100%"
            />
          </Flex>
        ))}
      </Flex>
      {errors.permissionsGroup?.[groupName] ? (
        <FormFeedback variant="error">Select an access level</FormFeedback>
      ) : null}
    </Flex>
  );
};

const AccessLevelsSection = ({
  groupedPhases,
  isLoading
}: {
  groupedPhases?: IGroupedWorkPhases;
  isLoading: boolean;
}) => {
  const { control } = useFormContext<IRoleFormData>();
  const permissionsGroup = useWatch({ control, name: 'permissionsGroup' });

  return (
    <Flex flexDirection="column" gap={3}>
      <FieldLabel
        label="Select Access Levels"
        subText="Choose the appropriate level of access in each workflow phase for this role."
        isRequired={false}
        helpText={<AccessLevelsHelp />}
      />
      {isLoading ? (
        <Spinner />
      ) : (
        (Object.keys(groupedPhases || {}) as IWorkPhase[]).map(groupName => (
          <AccessLevelGroup
            key={groupName}
            groupName={groupName}
            selectedLevel={permissionsGroup?.[groupName]}
          />
        ))
      )}
    </Flex>
  );
};

type RoleFormProps = {
  workspaceId: string;
  title: string;
  submitLabel: string;
  defaultValues: IRoleFormData;
  onSubmit: (values: IRolePayload) => Promise<void>;
  onCancel: () => void;
  isNameReadOnly?: boolean;
};

/**
 * Shared role form used by both the create and the update role screens. It owns
 * the phase permissions fields, the validation and the submit state; the
 * screens only provide the labels and what to do with the submitted values.
 */
export const RoleForm: React.FC<RoleFormProps> = ({
  workspaceId,
  title,
  submitLabel,
  defaultValues,
  onSubmit,
  onCancel,
  isNameReadOnly = false
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['workspace-phases', { workspaceId }],
    queryFn: () => fetchWorkspaceEditorialPhases(workspaceId),
    retry: 0
  });

  const methods = useForm<IRoleFormData>({
    defaultValues,
    resolver: zodResolver(roleFormSchema)
  });

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = methods;

  const handleValidSubmit = async ({
    name,
    displayName,
    permissionsGroup
  }: IRoleFormData) => {
    setIsSubmitting(true);
    try {
      await onSubmit({
        name,
        displayName,
        permissions: buildPhasePermissions(data?.grouped, permissionsGroup)
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Overlay>
      <Layout
        width={{ base: '100%', lg: '960px' }}
        paddingX={{ base: 4, md: 6, lg: 0 }}
      >
        <Box mb={2}>
          <ButtonText onClick={onCancel}>
            <FontAwesomeIcon icon={faAngleLeft} />
            Back to Roles
          </ButtonText>
        </Box>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleValidSubmit, scrollToFirstError)}>
            <Text textStyle="headingTitle">{title}</Text>
            <Flex flexDirection="column" gap={6} marginTop={6}>
              <Flex flexDirection="column" gap={2}>
                <FieldLabel
                  label="Name"
                  subText="Use a clear unique name to help your teams identify the role's purpose."
                />
                <Input
                  {...register('name', { setValueAs: value => value.trim() })}
                  placeholder="Enter the name of the role"
                  isInvalid={Boolean(errors.name)}
                  isDisabled={isNameReadOnly}
                />
                {errors.name?.message ? (
                  <FormFeedback variant="error">
                    {errors.name.message}
                  </FormFeedback>
                ) : null}
              </Flex>

              <Flex flexDirection="column" gap={2}>
                <FieldLabel
                  label="Display Name"
                  subText="A display label used for this role."
                />
                <Input
                  {...register('displayName')}
                  placeholder="Enter the display name of the role"
                  isInvalid={Boolean(errors.displayName)}
                />
                {errors.displayName?.message ? (
                  <FormFeedback variant="error">
                    {errors.displayName.message}
                  </FormFeedback>
                ) : null}
              </Flex>

              <AccessLevelsSection
                groupedPhases={data?.grouped}
                isLoading={isLoading}
              />

              {errors.permissions?.message && (
                <FormFeedback variant="error">
                  {errors.permissions.message}
                </FormFeedback>
              )}
            </Flex>

            <Flex
              justifyContent={{ base: 'stretch', sm: 'flex-end' }}
              flexDirection={{ base: 'column-reverse', sm: 'row' }}
              gap={2}
              alignItems={{ base: 'stretch', sm: 'center' }}
              marginTop={8}
            >
              <Button
                variant="secondary"
                onClick={onCancel}
                isDisabled={isSubmitting}
                width={{ base: '100%', sm: 'auto' }}
              >
                Exit Without Saving
              </Button>
              <Button
                type="submit"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                width={{ base: '100%', sm: 'auto' }}
              >
                {submitLabel}
              </Button>
            </Flex>
          </form>
        </FormProvider>
      </Layout>
    </Overlay>
  );
};
